import { Injectable } from "@angular/core";
import { Http, Response } from "@angular/http";
import { Observable } from "rxjs/Observable";
import { CompaniesService } from "../companies/companies.service";
import { ApiService } from "../shared";
import { ConfigService } from "../config.service";
import * as Joi from "joi";

export enum errorTypes {
  usedProjectCode = 0
}

@Injectable()
export class ProjectsService {
  static currentProject: any;
  private serviceUrl = ConfigService.serviceUrl;
  private supplierSchema: Joi.Schema = Joi.object().keys({
    company_id: Joi.string().min(1).required(),
    type: Joi.string().min(1).required(),
    active: Joi.bool().required(),
    start_date: Joi.date().iso().min(1),
    end_date: Joi.date().iso().min(Joi.ref("start_date")),
    suppliers: Joi.array().items(Joi.lazy(() => this.supplierSchema))
  });
  private projectSchema: Joi.Schema = Joi.object().keys({
    name: Joi.string().min(1).required(),
    project_code: Joi.string().min(1).required(),
    program_id: Joi.string(),
    active: Joi.bool(),
    country: Joi.string().allow(null),
    description: Joi.string().min(1).required(),
    client: Joi.object()
      .required()
      .keys({
        company_id: Joi.string().min(1).required()
      }),
    supervisors: Joi.array()
      .required()
      .items(
        Joi.object().keys({
          company_id: Joi.string().min(1).required(),
          role: Joi.string().min(1).required()
        })
      )
      .min(1),
    suppliers: Joi.array().required().items(this.supplierSchema).min(1),
    id: Joi.string(),
    created_at: Joi.date(),
    updated_at: Joi.date()
  });
  projects: any[];

  constructor(private http: Http, private companiesService: CompaniesService) {}

  private extractData(res: Response, getnames: boolean) {
    let body = res.json();
    if (getnames) {
      this.getEmployerNames(body);
    }
    return body;
  }

  private handleError(error: Response | any) {
    let errMsg: string;
    if (error instanceof Response) {
      const body = error.json() || "";
      const err = body.error || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ""} ${err}`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    console.error(errMsg);
    return Observable.throw(errMsg);
  }

  getProjects(): Observable<any[]> {
    return this.http
      .get(`${this.serviceUrl}/projects`)
      .map((res) => {
        return this.extractData(res, true);
      })
      .catch(this.handleError);
  }

  getProjectList(): Observable<any> {
    if (this.projects) {
      return Observable.create((observer) => {
        observer.next(this.projects);
        observer.complete();
      });
    } else {
      return Observable.create((observer) => {
        this.getProjects().subscribe((projects) => {
          projects.sort((a, b) => {
            if (a.name.toLowerCase() > b.name.toLowerCase()) {
              return 1;
            }
            return -1;
          });
          this.projects = projects;
          observer.next(projects);
          observer.complete();
        });
      });
    }
  }

  getItemsPerPageValues(): number[] {
    return ConfigService.itemsPerPageValues;
  }

  getItemsPerPage(): number {
    return ApiService.getItemsPerPage();
  }

  saveItemsPerPage(value: number) {
    ApiService.saveItemsPerPage(value);
  }

  getEmployerNames(projects: any[]) {
    this.companiesService.getEmployers().subscribe((employerList) => {
      projects.map((project) => {
        project.supervisorsNames = [];
        project.suppliersNames = [];
        if (project.supervisors.length < 1) {
          project.supervisorsNames = ["<any>"];
        } else {
          project.supervisors.map((supervisor) => {
            let employers = employerList.filter((it) => {
              return it.id === supervisor.company_id;
            });
            if (employers.length > 0) {
              project.supervisorsNames.push(employers[0].name);
            }
          });
        }
        if (project.suppliers.length < 1) {
          project.suppliersNames = ["<any>"];
        } else {
          project.suppliers.map((supplier) => {
            let employers = employerList.filter((it) => {
              return it.id === supplier.company_id;
            });
            if (employers.length > 0) {
              project.suppliersNames.push(employers[0].name);
            }
          });
        }
        if (!project.client) {
          project.clientName = "<any>";
        } else {
          let companies = employerList.filter((it) => {
            return it.id === project.client.company_id;
          });
          if (companies.length > 0) {
            project.clientName = companies[0].name;
          }
        }
      });
    });
  }

  getCompanies() {
    return this.companiesService.getEmployers();
  }

  getNationalities(): any[] {
    return ConfigService.countryList;
  }

  getProject(id) {
    return this.http.get(`${this.serviceUrl}/projects/${id}`).map((res) => {
      return this.extractData(res, false);
    });
  }

  createProject(project: any): Observable<any> {
    return Observable.create((observer) => {
      if (!project.country || project.country == "null") {
        project.country = null;
      }
      Joi.validate(project, this.projectSchema, (err, v) => {
        if (err) {
          observer.error(err);
          observer.complete();
        } else {
          this.getProjects().subscribe((projects) => {
            if (
              projects.find((it) => {
                return it.project_code === project.project_code;
              })
            ) {
              observer.error({ errorType: errorTypes.usedProjectCode });
              observer.complete();
            } else {
              this.http.post(`${this.serviceUrl}/projects`, project).subscribe(
                (resp) => {
                  observer.next(this.extractData(resp, null));
                  observer.complete();
                },
                (error) => {
                  observer.error(this.handleError(error));
                  observer.complete();
                }
              );
            }
          });
        }
      });
    });
  }

  editProject(project: any): Observable<any> {
    delete project.supervisorsNames;
    delete project.suppliersNames;
    delete project.clientName;
    return Observable.create((observer) => {
      if (!project.country || project.country == "null") {
        project.country = null;
      }
      Joi.validate(project, this.projectSchema, (err, v) => {
        if (err) {
          observer.error(err);
          observer.complete();
        } else {
          this.getProjects().subscribe((projects) => {
            if (
              projects.find((it) => {
                return (
                  it.project_code === project.project_code &&
                  it.id !== project.id
                );
              })
            ) {
              observer.error({ errorType: errorTypes.usedProjectCode });
              observer.complete();
            } else {
              this.http
                .put(`${this.serviceUrl}/projects/` + project.id, project)
                .subscribe(
                  (resp) => {
                    observer.next(this.extractData(resp, null));
                    observer.complete();
                  },
                  (error) => {
                    observer.error(this.handleError(error));
                    observer.complete();
                  }
                );
            }
          });
        }
      });
    });
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/projects/projects.service.ts